import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Table, Spinner, Button, Alert, Form, Row, Col } from 'react-bootstrap';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import useAuth from '../context/useAuth';

const DonationReportByYear = () => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [year, setYear] = useState(''); // Default year
    const [sortBy, setSortBy] = useState('first1');
    const [currentPage, setCurrentPage] = useState(1);
    const { role, resultsPerPage } = useAuth();
    const [pageGroupSize] = useState(10); // Show only x pages at a time

    useEffect(() => {
        const fetchDonations = async () => {
            setLoading(true);
            try {
                const queryParams = new URLSearchParams();
                if (year) queryParams.append('year', year);
                if (sortBy) queryParams.append('sortBy', sortBy);
                const response = await api.get(`/api/donationsByYear?${queryParams.toString()}`);
                setMembers(response.data);
                setCurrentPage(1); // Reset pagination
            } catch (err) {
                setError('Failed to load donations. Please try again.');
                console.error("Donation fetch error:", err); // Optional logging to external service
            } finally {
                setLoading(false);
            }
        };
        fetchDonations();
    }, [year, sortBy]);

    // Custom function to check if any donations exist
    const hasDonations = members.length > 0;

    const years = Array.from({ length: new Date().getFullYear() - 1991 }, (_, i) => 1992 + i);

    // Pagination logic
    const indexOfLastMember = currentPage * resultsPerPage;
    const indexOfFirstMember = indexOfLastMember - resultsPerPage;
    const currentMembers = members.slice(indexOfFirstMember, indexOfLastMember);

    const totalPages = Math.ceil(members.length / resultsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const getPageNumbers = () => {
        const startPage = Math.floor((currentPage - 1) / pageGroupSize) * pageGroupSize + 1;
        const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);
        return Array.from({ length: endPage - startPage + 1 }, (_, idx) => startPage + idx);
    };

    const handleFirstPage = () => setCurrentPage(1);
    const handleLastPage = () => setCurrentPage(totalPages);
    const handlePreviousPageGroup = () => setCurrentPage(Math.max(currentPage - pageGroupSize, 1));
    const handleNextPageGroup = () => setCurrentPage(Math.min(currentPage + pageGroupSize, totalPages));

    const getLocalTimestamp = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
    };

    const getReportTitle = () => {
        let title = 'Member Donation Report Summary';
        if (year) title += ` for ${year}`;
        else title += ' by Year';
        return title;
    };

    const handleDownloadPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' }); // Landscape mode for width
        doc.text(getReportTitle(), 20, 10);

        const body = [];
        const selectedYear = year; // Assuming 'year' state holds the selected year value

        members.forEach((member) => {
            if (selectedYear) {

                body.push([
                    member.memberDetails.memberId,
                    `${member.memberDetails.first1} ${member.memberDetails.middle1} ${member.memberDetails.last1}`,
                    `${member.memberDetails.address} ${member.memberDetails.address2}`,
                    member.memberDetails.city,
                    member.memberDetails.state,
                    new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                    }).format(parseFloat(member.memberDetails.lifetimeDonation || 0)),
                    new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                    }).format(parseFloat(member.yearlyDonations[0]?.totalDonations || 0)),
                ]);


            } else {
                // If no specific year is selected, show member details and yearly donations separately
                // Add member details row
                body.push([
                    member.memberDetails.memberId,
                    `${member.memberDetails.first1} ${member.memberDetails.middle1} ${member.memberDetails.last1}`,
                    `${member.memberDetails.address} ${member.memberDetails.address2}`,
                    member.memberDetails.city,
                    member.memberDetails.state,
                    'Lifetime', // Empty cell for Year (to be filled in yearly donations)
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(member.memberDetails.lifetimeDonation)),
                ]);

                // Add yearly donations rows
                (member.yearlyDonations || []).forEach((donation) => {
                    body.push([
                        '', // Empty cell for Member ID
                        '', // Empty cell for Member Name
                        '', // Empty cell for Address
                        '', // Empty cell for City
                        '', // Empty cell for State
                        donation.donationYear,
                        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(donation.totalDonations)),
                    ]);
                });
            }
        });

        if (selectedYear) {
            doc.autoTable({
                startY: 20,
                head: [['Member ID', 'Member Name', 'Address', 'City', 'State', 'Lifetime Donation', `Total Donations in ${year}`]],
                body,
                theme: 'striped',
                styles: { fontSize: 10 }, // Adjust font size
            });
        }
        else {
            doc.autoTable({
                startY: 20,
                head: [['Member ID', 'Member Name', 'Address', 'City', 'State', 'Year', 'Total Donations']],
                body,
                theme: 'striped',
                styles: { fontSize: 10 }, // Adjust font size
            });
        }

        doc.save(`Member_Donation_Report_${getLocalTimestamp()}.pdf`);
    };

    const handlePrintAll = () => {
        const chunkSize = 100; // Define chunk size for batch rendering
        const totalChunks = Math.ceil(members.length / chunkSize);
        const selectedYear = year; // Assuming 'year' is coming from the state

        let printWindow = window.open('', '_blank'); // Open new window for printing
        let docContent = '';

        // Render data in chunks
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const start = chunkIndex * chunkSize;
            const end = start + chunkSize;
            const chunk = members.slice(start, end);

            // Build table HTML for this chunk
            let tableRows = '';
            chunk.forEach(member => {
                if (selectedYear) {
                    // Logic when a specific year is selected

                    tableRows += `
                        <tr>
                            <td>${member.memberDetails.memberId}</td>
                            <td>${member.memberDetails.first1} ${member.memberDetails.last1}</td>
                            <td>${member.memberDetails.address}</td>
                            <td>${member.memberDetails.city}</td>
                            <td>${member.memberDetails.state}</td>
                            <td>${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(member.memberDetails.lifetimeDonation)}</td>    
                            <td>${new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                    }).format(parseFloat(member.yearlyDonations[0]?.totalDonations || 0))}</td>
                        </tr>
                    `;
                } else {
                    // Logic when no specific year is selected
                    tableRows += `
                        <tr>
                            <td>${member.memberDetails.memberId}</td>
                            <td>${member.memberDetails.first1} ${member.memberDetails.last1}</td>
                            <td>${member.memberDetails.address}</td>
                            <td>${member.memberDetails.city}</td>
                            <td>${member.memberDetails.state}</td>
                            <td>Lifetime</td>
                            <td>${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(member.memberDetails.lifetimeDonation)}</td>   
                        </tr>
                    `;

                    (member.yearlyDonations || []).forEach(donation => {
                        tableRows += `
                            <tr>
                                <td></td> <!-- Empty Member ID -->
                                <td></td> <!-- Empty Member Name -->
                                <td></td> <!-- Empty Address -->
                                <td></td> <!-- Empty City -->
                                <td></td> <!-- Empty State -->
                                <td>${donation.donationYear}</td>
                                <td>${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(donation.totalDonations)}</td>
                            </tr>
                        `;
                    });
                }
            });

            // Append this chunk's table to the content
            docContent += `
                <table border="1">
                    <thead>
                        <tr>
                            <th>Member ID</th>
                            <th>Name</th>
                            <th>Address</th>
                            <th>City</th>
                            <th>State</th>
                            <th>${selectedYear ? 'Lifetime Donation' : 'Year'}</th>
                            <th>${selectedYear ? `Total Donations in ${selectedYear}` : 'Yearly Donations'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>
                <hr />
            `;
        }

        // Write the content into the new print window
        printWindow.document.write(`
            <html>
            <head>
                <title>Print Member Report By Year</title>
                <style>
                    @media print {
                        table {
                            width: 100%;
                            border-collapse: collapse;
                        }
                        th, td {
                            padding: 8px;
                            text-align: left;
                            border: 1px solid black;
                        }
                    }
                </style>
            </head>
            <body>
                ${docContent}
            </body>
            </html>
        `);

        printWindow.document.close(); // Close document writing
        printWindow.focus(); // Focus on the new window

        // Print the document and close the window after a delay
        printWindow.print();

        // Use setTimeout to delay closing of the window after print dialog appears
        setTimeout(() => {
            printWindow.close();
        }, 1000); // Adjust the timeout as needed
    };

    const handleDownloadCSV = () => {
        const csvRows = [];
        const headers = [
            'Member ID',
            'First Name',
            'Middle Name',
            'Last Name',
            'Address',
            'Address2',
            'City',
            'State',
            year ? 'Lifetime Donation' : 'Year',
            year ? `Total Donations in ${year}` : 'Total Donations'
        ];
        csvRows.push(headers.join(','));

        const selectedYear = year; // Assuming 'year' state holds the selected year value

        members.forEach(member => {
            if (selectedYear) {
                csvRows.push([
                    member.memberDetails.memberId,
                    `"${member.memberDetails.first1}"`,
                    `"${member.memberDetails.middle1}"`,
                    `"${member.memberDetails.last1}"`,
                    `"${member.memberDetails.address}"`,
                    `"${member.memberDetails.address2}"`,
                    `"${member.memberDetails.city}"`,
                    `"${member.memberDetails.state}"`,
                    member.memberDetails.lifetimeDonation ? member.memberDetails.lifetimeDonation : 0,
                    member.yearlyDonations[0]?.totalDonations || 0,
                ].join(','));

            } else {
                // If no specific year is selected, show member details and yearly donations separately
                // Add member details
                csvRows.push([
                    member.memberDetails.memberId,
                    `"${member.memberDetails.first1}"`,
                    `"${member.memberDetails.middle1}"`,
                    `"${member.memberDetails.last1}"`,
                    `"${member.memberDetails.address}"`,
                    `"${member.memberDetails.address2}"`,
                    `"${member.memberDetails.city}"`,
                    `"${member.memberDetails.state}"`,
                    '', // Empty cell for Year (to be filled in yearly donations)
                    member.memberDetails.lifetimeDonation ? member.memberDetails.lifetimeDonation : 0
                ].join(','));

                // Add yearly donations
                (member.yearlyDonations || []).forEach(donation => {
                    csvRows.push([
                        '', // Empty cell for Member ID
                        '', // Empty cell for First Name
                        '', // Empty cell for Middle Name
                        '', // Empty cell for Last Name
                        '', // Empty cell for Address
                        '', // Empty cell for Address2
                        '', // Empty cell for City
                        '', // Empty cell for State
                        donation.donationYear,
                        donation.totalDonations ? donation.totalDonations : 0
                    ].join(','));
                });
            }
        });

        const csvData = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const csvURL = window.URL.createObjectURL(csvData);

        const tempLink = document.createElement('a');
        // Generate a timestamp for the filename
        const timestamp = getLocalTimestamp();
        const filename = `Member_Donation_Report_${timestamp}.csv`;
        tempLink.href = csvURL;
        tempLink.setAttribute('download', filename);
        tempLink.click();
    };

    if (loading) return <Spinner animation="border" />;
    if (error) return <Alert variant="danger">{error}</Alert>;

    return (
        <div>
            {role === "admin" && (
                <div className="d-flex justify-content-end mb-2">
                    <Button className="button-spacing no-print small-font me-2" onClick={handleDownloadPDF}>
                        Download PDF
                    </Button>
                    <Button className="button-spacing no-print small-font me-2" onClick={handleDownloadCSV}>
                        Download CSV
                    </Button>
                    <Button className="button-spacing no-print small-font" onClick={handlePrintAll}>
                        Print
                    </Button>
                </div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <Row className="w-100">
                    <Col md={6} className="d-flex align-items-center">
                        <label className="me-2 small-font font-weight-bold" htmlFor="year">Donation Year (Optional)</label>
                        <select
                            id="year"
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            className="form-select small-font font-weight-bold"
                        >
                            <option value="">Select Year</option>
                            {years.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </Col>
                </Row>
                <Row className="w-100 mt-2">
                    <Col md={6} className="d-flex align-items-center">
                        <label className="form-label small-font font-weight-bold me-2">
                            Sort by
                        </label>
                    </Col>
                    <Col md={6} className="d-flex align-items-center radio-box">
                        <Form.Check
                            type="radio"
                            id="sort-by-first-name"
                            name="sortBy"
                            value="first1"
                            label="First Name"
                            checked={sortBy === 'first1'}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="small-font font-weight-bold me-2"
                            inline
                        />
                        <Form.Check
                            type="radio"
                            id="sort-by-last-name"
                            name="sortBy"
                            value="last1"
                            label="Last Name"
                            checked={sortBy === 'last1'}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="small-font font-weight-bold"
                            inline
                        />
                    </Col>
                </Row>
            </div>
            <div className="container">
                <h2>{getReportTitle()}</h2>
                <Table striped bordered hover>
                    <thead>
                        {year ? (
                            // Header for the specific year view
                            <tr>
                                <th>Member ID</th>
                                <th>Member Name</th>
                                <th>Address</th>
                                <th>City</th>
                                <th>State</th>
                                <th>Lifetime Donation</th>
                                <th>Total Donation in {year}</th>
                            </tr>
                        ) : (
                            // Header for the grouped view
                            <tr>
                                <th>Member ID</th>
                                <th>Member Name</th>
                                <th>Address</th>
                                <th>City</th>
                                <th>State</th>
                                <th>Year</th>
                                <th>Total Yearly Donations</th>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {hasDonations ? (
                            year ? (
                                // If a specific year is selected, show a single row with all fields
                                <>
                                    {currentMembers.map((member) => (
                                        <tr key={member.memberDetails.memberId}>
                                            <td>{member.memberDetails.memberId}</td>
                                            <td>{`${member.memberDetails.first1} ${member.memberDetails.middle1} ${member.memberDetails.last1}`}</td>
                                            <td>{`${member.memberDetails.address} ${member.memberDetails.address2}`}</td>
                                            <td>{member.memberDetails.city}</td>
                                            <td>{member.memberDetails.state}</td>
                                            <td>{new Intl.NumberFormat('en-US', {
                                                style: 'currency',
                                                currency: 'USD',
                                            }).format(parseFloat(member.memberDetails.lifetimeDonation || 0))}</td>
                                            <td>
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: 'USD',
                                                }).format(parseFloat(member.yearlyDonations[0]?.totalDonations || 0))}
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            ) : (
                                // If no year is selected, show grouped yearly donations
                                <>
                                    {currentMembers.map((member) => (
                                        <React.Fragment key={member.memberDetails.memberId}>
                                            {/* Render member details only once */}
                                            <tr>
                                                <td>{member.memberDetails.memberId}</td>
                                                <td>{`${member.memberDetails.first1} ${member.memberDetails.middle1} ${member.memberDetails.last1}`}</td>
                                                <td>{`${member.memberDetails.address} ${member.memberDetails.address2}`}</td>
                                                <td>{member.memberDetails.city}</td>
                                                <td>{member.memberDetails.state}</td>
                                                <td>Lifetime</td>
                                                <td style={{ fontWeight: 'bold' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(parseFloat(member.memberDetails.lifetimeDonation))}
                                                </td>
                                            </tr>
                                            {/* Render yearly donations for the member */}
                                            {(member.yearlyDonations || []).map((donation) => (
                                                <tr key={`${member.memberDetails.memberId}-${donation.donationYear}`}>
                                                    <td colSpan={5}></td> {/* Empty cells to align with member details */}
                                                    <td>{donation.donationYear}</td>
                                                    <td>{new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(parseFloat(donation.totalDonations))}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </>
                            )
                        ) : (
                            <tr>
                                <td colSpan="7" className="text-center">No member donations found for the selected year and criteria.</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Pagination Controls */}
            <div className="d-flex justify-content-center mt-4 no-print">
                <Button onClick={handleFirstPage} disabled={currentPage === 1}>
                    &lt;&lt;
                </Button>
                <Button onClick={handlePreviousPageGroup} disabled={currentPage <= pageGroupSize}>
                    &lt;
                </Button>

                {currentPage > pageGroupSize && <span>...</span>}

                {getPageNumbers().map(number => (
                    <Button
                        key={number}
                        onClick={() => paginate(number)}
                        className={number === currentPage ? 'active bg-primary text-white' : ''}
                    >
                        {number}
                    </Button>
                ))}

                {currentPage + pageGroupSize <= totalPages && <span>...</span>}

                <Button onClick={handleNextPageGroup} disabled={currentPage + pageGroupSize > totalPages}>
                    &gt;
                </Button>
                <Button onClick={handleLastPage} disabled={currentPage === totalPages}>
                    &gt;&gt;
                </Button>
            </div>
        </div>
    );

};

export default DonationReportByYear;